import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { api, ApiError, sessionStore, tokenStore, upvotedStore } from "../lib/api";
import { formatAskers } from "../lib/format";
import { subscribeToBoard } from "../lib/sse";

type Q = {
  id: string;
  body: string;
  status: string;
  // vote_count is voices+upvotes — used as the board sort key. The tile
  // shows voices and upvotes separately: voices = askers.length +
  // anonymous_count, upvotes = upvote_count.
  vote_count: number;
  upvote_count: number;
  askers: string[];
  anonymous_count: number;
  answered_at: string | null;
  answer_text: string | null;
  created_at: string;
};

type Candidate = {
  question_id: string;
  body: string;
  score: number;
  vote_count: number;
  voices_count: number;
  upvote_count: number;
  status: string;
};

// One in-flight draft from another attendee in this session. ``preview``
// is shaped server-side by the session's drafts_mode: null in generic
// mode (we render a fixed line), truncated text in preview mode, full
// text in full mode. We render whatever the backend sent.
type LiveDraft = {
  score: number;
  preview: string | null;
};

// Single integrated page: live approved questions on the board + an inline
// "Ask a question" composer that runs similarity (against any non-rejected
// question, so pending ones surface too). Submitting or joining either
// refreshes the board immediately and the SSE stream keeps everyone else in
// sync.
function formatCountdown(ms: number): string {
  if (ms <= 0) return "0s";
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

// State copy maps state → user-facing banner copy. Setup/paused/closed each
// have a distinct reason a visitor would care about; we keep the wording
// short so it sits naturally above the question list. When a scheduled
// start is set, the setup banner becomes a live countdown.
function stateBanner(
  state: string,
  startsAt: string | null,
  now: number,
): { tone: "info" | "warn"; text: string } | null {
  switch (state) {
    case "setup": {
      if (startsAt) {
        const remaining = new Date(startsAt).getTime() - now;
        if (remaining > 0) {
          return {
            tone: "info",
            text:
              `This event starts in ${formatCountdown(remaining)} `
              + "— you can draft a question now and it will submit automatically when the event opens.",
          };
        }
        // Time has elapsed but the lazy-open flip hasn't reached this client
        // yet (no /info refresh has fired since the timer expired). Soften
        // the copy so the user knows we're waiting to flip rather than that
        // nothing's scheduled.
        return {
          tone: "info",
          text: "Starting now — opening the event…",
        };
      }
      return {
        tone: "info",
        text:
          "This event hasn't started yet — you can draft a question now and "
          + "submit it once the organiser opens the event.",
      };
    }
    case "paused":
      return {
        tone: "warn",
        text:
          "The organiser has paused new questions — you can draft now and "
          + "submit when they resume.",
      };
    case "closed":
      return { tone: "warn", text: "This event is closed. Questions are read-only." };
    default:
      return null; // open
  }
}

export default function Board() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const stored = sessionStore.get();

  const [questions, setQuestions] = useState<Q[]>([]);
  const [err, setErr] = useState<string | null>(null);
  // Session metadata is fetched fresh from /info so the banner reflects
  // moderator transitions even if the cached session_state at join time is
  // stale (state can change between join and now).
  const [sessionMeta, setSessionMeta] = useState<{
    name: string;
    state: string;
    starts_at: string | null;
  } | null>(
    stored ? { name: stored.name, state: stored.state, starts_at: null } : null,
  );
  // Tick once per second so the countdown banner updates smoothly. Only
  // mounts the interval when there's a future starts_at to count toward —
  // saves render churn when the session is already open or unscheduled.
  const [now, setNow] = useState(() => Date.now());

  // Authoritative identity from the backend — overrides whatever
  // sessionStore had cached, which may be stale (older frontend builds
  // wrote no displayName/isAnonymous fields, so cached values default to
  // "Anonymous" even when the participant gave a name).
  const [me, setMe] = useState<{
    displayName: string | null;
    isAnonymous: boolean;
  } | null>(
    stored
      ? { displayName: stored.displayName, isAnonymous: stored.isAnonymous }
      : null,
  );

  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  // Live in-flight drafts from other typers — populated when the session
  // has drafts_mode != "off". Empty otherwise.
  const [liveDrafts, setLiveDrafts] = useState<LiveDraft[]>([]);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  // Question id of the most recently submitted-and-still-pending
  // question, used to auto-dismiss the "Submitted — awaiting moderation"
  // banner once the moderator approves (or rejects) it. Cleared by the
  // effect below as soon as the question transitions out of `pending`.
  const [pendingSubmittedQid, setPendingSubmittedQid] = useState<string | null>(null);
  // Free-text + semantic search over the visible board. Substring
  // filtering is instant; the backend semantic ranking arrives after
  // a brief debounce and reorders the filtered list by relevance.
  // Empty backend response (embedding/qdrant failure) silently falls
  // back to substring-only.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchScores, setSearchScores] = useState<Map<string, number> | null>(
    null,
  );
  useEffect(() => {
    if (!sessionId) return;
    const q = searchQuery.trim();
    if (!q) {
      setSearchScores(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const hits = await api.boardSearch(sessionId, q);
        if (cancelled) return;
        if (hits.length === 0) {
          setSearchScores(null);
          return;
        }
        const next = new Map<string, number>();
        for (const h of hits) next.set(h.question_id, h.score);
        setSearchScores(next);
      } catch {
        if (!cancelled) setSearchScores(null);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchQuery, sessionId]);
  // Question ids this attendee has voted on (or joined — "Add my name" also
  // counts as a vote server-side). Seeded from sessionStorage so a page
  // refresh keeps the button in its "voted" state.
  const [upvoted, setUpvoted] = useState<Set<string>>(() =>
    sessionId ? new Set(upvotedStore.list(sessionId)) : new Set(),
  );
  const debounceRef = useRef<number | null>(null);
  const seqRef = useRef(0);

  const markUpvoted = useCallback(
    (qid: string) => {
      if (!sessionId) return;
      upvotedStore.add(sessionId, qid);
      setUpvoted((prev) => {
        if (prev.has(qid)) return prev;
        const next = new Set(prev);
        next.add(qid);
        return next;
      });
    },
    [sessionId],
  );

  const isOpen = sessionMeta?.state === "open";
  // Drafting (opening the composer + typing) is allowed in any state
  // except `closed` — closed events are read-only by design. Submission
  // is gated separately on `isOpen`. This lets visitors stage a question
  // before the event starts or while the organiser has paused, instead
  // of staring at a disabled button.
  const draftingAllowed = !!sessionMeta && sessionMeta.state !== "closed";
  const banner = sessionMeta
    ? stateBanner(sessionMeta.state, sessionMeta.starts_at, now)
    : null;
  const sessionName = sessionMeta?.name ?? "Live questions";

  const refreshInfo = useCallback(async () => {
    if (!sessionId) return;
    try {
      const r = await api.sessionInfo(sessionId);
      setSessionMeta({ name: r.name, state: r.state, starts_at: r.starts_at });
    } catch {
      // best-effort; leave any cached metadata in place
    }
  }, [sessionId]);

  // Countdown ticker: only run while we have a future starts_at and we're
  // still in setup. When the timer crosses zero, fetch /info — that read
  // path triggers the lazy auto-open server-side and returns the new state.
  useEffect(() => {
    if (!sessionMeta || sessionMeta.state !== "setup" || !sessionMeta.starts_at) return;
    const target = new Date(sessionMeta.starts_at).getTime();
    let elapsedFired = false;
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (!elapsedFired && t >= target) {
        elapsedFired = true;
        // Trigger the lazy auto-open by refetching info; even if the
        // server hasn't seen any other request, this one does the flip.
        refreshInfo();
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [sessionMeta, refreshInfo]);

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    try {
      const rows = await api.board(sessionId);
      // Ranking tiers, top → bottom:
      //   1. Pending (visible only to the asker, newest first)
      //   2. Approved & not yet answered (by vote_count desc, then
      //      recency)
      //   3. Answered (still visible for context, but pushed below the
      //      live queue since they no longer need attention; same
      //      vote/recency sort within the tier)
      // The previous sort didn't differentiate tier 2 from tier 3, so
      // a heavily upvoted answered question kept its top slot — that
      // pushed live questions down and felt wrong once the moderator
      // had moved on.
      rows.sort((a, b) => {
        const aPending = a.status === "pending";
        const bPending = b.status === "pending";
        if (aPending && !bPending) return -1;
        if (!aPending && bPending) return 1;
        if (aPending && bPending) return b.created_at.localeCompare(a.created_at);
        const aAnswered = !!a.answered_at;
        const bAnswered = !!b.answered_at;
        if (aAnswered !== bAnswered) return aAnswered ? 1 : -1;
        return (
          b.vote_count - a.vote_count
          || a.created_at.localeCompare(b.created_at)
        );
      });
      setQuestions(rows);
    } catch {
      setErr("Could not load board");
    }
  }, [sessionId]);

  useEffect(() => {
    refresh();
    refreshInfo();
  }, [refresh, refreshInfo]);

  // Resolve attendee identity from the backend. Works for fresh joins (the
  // bearer is set in tokenStore by Join.tsx before navigating here) and also
  // for users who joined under a previous frontend build that didn't cache
  // displayName/isAnonymous in sessionStorage.
  useEffect(() => {
    if (!tokenStore.get()) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.me();
        if (cancelled) return;
        setMe({ displayName: r.display_name, isAnonymous: r.is_anonymous });
      } catch {
        // best-effort; if /me fails we fall back to whatever sessionStore had
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    const off = subscribeToBoard(sessionId, (type) => {
      if (type === "state_changed") {
        refreshInfo();
      } else {
        refresh();
      }
    });
    return off;
  }, [sessionId, refresh, refreshInfo]);

  // Auto-dismiss the "Submitted — awaiting moderation" banner once
  // the tracked question is no longer pending. Triggered by every
  // SSE-driven refresh (question_moderated / question_rejected both
  // mutate `questions`), so the banner clears as soon as the moderator
  // approves or rejects. Replacing the text with a brief positive
  // confirmation when approved gives the asker closure; on rejection
  // we just clear silently because rejection messaging is the
  // moderator's call.
  useEffect(() => {
    if (!pendingSubmittedQid) return;
    const row = questions.find((q) => q.id === pendingSubmittedQid);
    if (!row) {
      // Row vanished — typically a rejection (pending rows are
      // visible only to the asker until approved, so disappearance
      // after submit means it was rejected before approval).
      setSuccess(null);
      setPendingSubmittedQid(null);
      return;
    }
    if (row.status !== "pending") {
      // Approved (or some other non-pending status): replace the
      // banner with a one-shot confirmation that it's live.
      setSuccess("Your question is live on the board.");
      setPendingSubmittedQid(null);
    }
  }, [questions, pendingSubmittedQid]);

  // Live similarity probe. Goal is that matches feel dynamic — surfacing
  // as the user types rather than after a stop-and-wait pause.
  //
  // Trigger rule:
  //   * skip until the draft has at least MIN_WORDS_FOR_PROBE words;
  //   * if the last char is whitespace, fire immediately — the user just
  //     finished a word, which is the moment new context becomes available
  //     to the embedding;
  //   * otherwise debounce briefly (DEBOUNCE_MS) so mid-word edits still
  //     probe quickly but rapid key-mashing coalesces into one request.
  //
  // The probe doubles as the draft-streaming step in Phase 5: when
  // drafts_mode is enabled server-side, this same call upserts the typer's
  // draft into the broker AND searches for other attendees' in-flight
  // drafts. Firing more often is fine: the broker upsert is idempotent on
  // (session_id, participant_id) so the latest text just replaces the
  // previous entry.
  useEffect(() => {
    if (!sessionId || !composerOpen) return;
    const trimmed = draft.trim();
    // Split on any whitespace; filter empty so trailing/leading spaces
    // don't inflate the count.
    const wordCount =
      trimmed.length === 0
        ? 0
        : trimmed.split(/\s+/).filter(Boolean).length;
    const MIN_WORDS_FOR_PROBE = 3;
    const DEBOUNCE_MS = 250;
    if (wordCount < MIN_WORDS_FOR_PROBE) {
      setCandidates([]);
      setLiveDrafts([]);
      return;
    }
    const justFinishedWord = /\s$/.test(draft);
    const delay = justFinishedWord ? 0 : DEBOUNCE_MS;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const seq = ++seqRef.current;
    debounceRef.current = window.setTimeout(async () => {
      try {
        const r = await api.similarity(sessionId, trimmed);
        if (seq === seqRef.current) {
          setCandidates(r.candidates);
          setLiveDrafts(r.drafts ?? []);
        }
      } catch {
        // Similarity is best-effort; don't block submitting if the probe fails.
      }
    }, delay);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [draft, sessionId, composerOpen]);

  function resetComposer() {
    setDraft("");
    setCandidates([]);
    setLiveDrafts([]);
  }

  async function upvote(qid: string) {
    if (!tokenStore.get()) {
      setErr("Join the event first to upvote.");
      return;
    }
    if (upvoted.has(qid)) return;
    setErr(null);
    try {
      // Use the response's counts for an immediate update so the user sees
      // their press reflected before the SSE-driven refresh fires.
      const updated = await api.upvote(qid);
      markUpvoted(qid);
      setQuestions((prev) =>
        prev.map((row) =>
          row.id === qid
            ? {
                ...row,
                vote_count: updated.vote_count,
                upvote_count: updated.upvote_count,
              }
            : row,
        ),
      );
      setSuccess("Thanks — your vote is in.");
      await refresh();
    } catch {
      setErr("Could not upvote");
    }
  }

  async function submit() {
    if (!sessionId) return;
    if (!tokenStore.get()) {
      setErr("Join the event first to ask a question.");
      return;
    }
    setErr(null);
    setSuccess(null);
    setBusy(true);
    try {
      const created = await api.submitQuestion(sessionId, draft.trim());
      setSuccess("Submitted — your question is awaiting moderation.");
      // Track the just-submitted qid so the banner auto-dismisses once
      // the question is moderated. The effect watching `questions`
      // clears `success` + `pendingSubmittedQid` as soon as that row
      // transitions away from `pending`.
      setPendingSubmittedQid(created.id);
      resetComposer();
      setComposerOpen(false);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not submit");
    } finally {
      setBusy(false);
    }
  }

  async function joinExisting(qid: string) {
    if (!tokenStore.get()) {
      setErr("Join the event first to add your voice.");
      return;
    }
    setErr(null);
    setSuccess(null);
    setBusy(true);
    try {
      await api.joinQuestion(qid);
      // Joining records a Vote (type=join) — the backend treats that as
      // already-voted, so reflect it locally to keep the button truthful
      // when the joined question gets approved and shows up on the board.
      markUpvoted(qid);
      setSuccess("Added your voice to that question.");
      resetComposer();
      setComposerOpen(false);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not join question");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main>
      <div className="reading">
      <h1>{sessionName}</h1>
      {me && (
        <p style={{ marginTop: "0.25rem" }}>
          <span
            aria-label={
              me.isAnonymous
                ? "Joined anonymously"
                : `Joined as ${me.displayName ?? "(no name)"}`
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
              padding: "0.15rem 0.6rem",
              borderRadius: "999px",
              background: "var(--surface-elevated)",
              border: "1px solid var(--border)",
              color: "var(--fg-muted)",
              fontSize: "0.8rem",
              fontWeight: 500,
            }}
          >
            <span aria-hidden="true">👤</span>
            Joined as{" "}
            <strong style={{ color: "var(--fg)" }}>
              {me.isAnonymous
                ? "Anonymous"
                : me.displayName?.trim() || "Anonymous"}
            </strong>
          </span>
        </p>
      )}
      <p style={{ color: "var(--fg-muted)" }} aria-live="polite">
        {questions.filter((q) => q.status === "approved").length} approved · updates in real time
      </p>

      {banner && (
        <div
          role={banner.tone === "warn" ? "alert" : "status"}
          className={banner.tone === "warn" ? "error" : "card meta"}
          style={{ marginBottom: "1rem" }}
        >
          {banner.text}
        </div>
      )}

      {!composerOpen ? (
        <button
          onClick={() => {
            setSuccess(null);
            setErr(null);
            setComposerOpen(true);
          }}
          style={{ width: "100%", marginBottom: "1rem" }}
          disabled={!tokenStore.get() || !draftingAllowed}
          aria-expanded={false}
          title={
            !tokenStore.get()
              ? "Join the event first"
              : !draftingAllowed
              ? "This event is closed — questions are read-only"
              : !isOpen
              ? "Draft now; submit once the event opens"
              : undefined
          }
        >
          {isOpen ? "Ask a question" : "Draft a question"}
        </button>
      ) : (
        <section className="card" aria-label="Ask a question" style={{ marginBottom: "1rem" }}>
          <label htmlFor="q">Your question</label>
          <textarea
            id="q"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Type your question…"
            maxLength={2048}
            autoFocus
          />

          {liveDrafts.length > 0 && (
            <>
              <h2 style={{ marginTop: "1rem" }}>
                Others typing similar right now
              </h2>
              <ul style={{ listStyle: "none", padding: 0 }}>
                {liveDrafts.map((d, i) => (
                  <li
                    key={i}
                    className="card"
                    style={{
                      borderLeft: "3px dashed var(--accent)",
                      background:
                        "color-mix(in srgb, var(--accent) 6%, transparent)",
                      fontStyle: d.preview ? "italic" : "normal",
                    }}
                  >
                    <div className="meta row" style={{ marginBottom: "0.25rem" }}>
                      <span
                        aria-label="Live draft"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.25rem",
                          padding: "0.1rem 0.5rem",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          borderRadius: "999px",
                          background: "var(--accent)",
                          color: "var(--accent-fg)",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        Live
                      </span>
                      <span className="score">
                        {Math.round(d.score * 100)}% match
                      </span>
                    </div>
                    <div className="body">
                      {d.preview ?? "Someone is typing a similar question."}
                    </div>
                  </li>
                ))}
              </ul>
              <p
                className="meta"
                style={{ fontSize: "0.8rem", color: "var(--fg-muted)" }}
              >
                Drafts disappear once submitted or after a brief idle period.
                You can wait, or submit yours and let the moderator group them.
              </p>
            </>
          )}

          {candidates.length > 0 && (
            <>
              <h2 style={{ marginTop: "1rem" }}>Already being asked — add your name?</h2>
              <ul style={{ listStyle: "none", padding: 0 }}>
                {candidates.map((c) => (
                  <li key={c.question_id} className="card candidate">
                    <div className="body">{c.body}</div>
                    <div className="meta row">
                      <span className="score">{Math.round(c.score * 100)}% match</span>
                      <span>· {c.voices_count} {c.voices_count === 1 ? "voice" : "voices"}</span>
                      <span>· {c.upvote_count} 👍</span>
                      {c.status !== "approved" && (
                        <span
                          aria-label={`status: ${c.status}`}
                          style={{ marginLeft: "0.5rem", fontStyle: "italic" }}
                        >
                          · {c.status}
                        </span>
                      )}
                      <button
                        onClick={() => joinExisting(c.question_id)}
                        disabled={busy}
                        style={{ marginLeft: "auto" }}
                      >
                        Add my name
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="row" style={{ marginTop: "1rem", gap: "0.5rem" }}>
            <button
              onClick={submit}
              disabled={busy || draft.trim().length < 1 || !isOpen}
              style={{ flex: 1 }}
              title={
                !isOpen
                  ? "Submission opens once the organiser starts the event"
                  : undefined
              }
            >
              {!isOpen
                ? "Submit when the event opens"
                : candidates.length > 0
                ? "Submit anyway as a new question"
                : "Submit"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                resetComposer();
                setComposerOpen(false);
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {err && <p className="error" role="alert">{err}</p>}
      {success && <p className="success" role="status">{success}</p>}

      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
          flexWrap: "wrap",
          margin: "0.75rem 0",
        }}
      >
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Find a question…"
          aria-label="Filter and semantically sort questions"
          style={{ flex: 1, minWidth: "180px" }}
        />
        {searchQuery.trim() && (
          <button
            type="button"
            className="secondary"
            onClick={() => setSearchQuery("")}
            aria-label="Clear search"
            style={{ padding: "0.35rem 0.7rem" }}
          >
            Clear
          </button>
        )}
        {searchQuery.trim() && (
          <span
            className="meta"
            style={{ fontSize: "0.78rem", color: "var(--fg-muted)" }}
            aria-live="polite"
          >
            {searchScores
              ? "Matches + relevance sort"
              : "Substring match"}
          </span>
        )}
      </div>

      {(() => {
        const q = searchQuery.trim().toLowerCase();
        const filtered = q
          ? questions.filter((r) => r.body.toLowerCase().includes(q))
          : questions;
        const visible = (() => {
          if (!searchScores) return filtered;
          // Stable resort by relevance: rows present in the score map
          // come first (highest first); missing rows preserve their
          // original ordering at the bottom.
          return filtered.slice().sort((a, b) => {
            const sa = searchScores.get(a.id);
            const sb = searchScores.get(b.id);
            if (sa == null && sb == null) return 0;
            if (sa == null) return 1;
            if (sb == null) return -1;
            return sb - sa;
          });
        })();
        if (q && visible.length === 0) {
          return (
            <p className="meta" style={{ marginTop: "0.5rem" }}>
              No questions match &ldquo;{searchQuery}&rdquo;.
            </p>
          );
        }
        return (
          <ul style={{ listStyle: "none", padding: 0 }} aria-live="polite">
            {visible.map((q) => {
          const askersLine = formatAskers(q.askers, q.anonymous_count);
          const isAnswered = !!q.answered_at;
          const isPending = q.status === "pending";
          const voicesCount = q.askers.length + q.anonymous_count;
          const hasVoted = upvoted.has(q.id);
          const canVote = !!tokenStore.get();
          // Pending rows are only ever visible to the asker themself; the
          // visual treatment marks them as in-flight (dimmed, dashed
          // accent, no upvote button) so the asker sees their submission
          // landed but the row is clearly different from an approved one.
          const accentBorder = isPending
            ? "3px dashed #d97706"
            : isAnswered
            ? "3px solid #16a34a"
            : undefined;
          return (
            <li
              key={q.id}
              className="card"
              style={{
                position: "relative",
                ...(accentBorder ? { borderLeft: accentBorder } : null),
                ...(isAnswered ? { opacity: 0.85 } : null),
                ...(isPending ? { opacity: 0.85 } : null),
              }}
            >
              {isPending ? (
                <span
                  aria-label="Awaiting moderation"
                  style={{
                    position: "absolute",
                    top: "0.5rem",
                    right: "0.5rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    padding: "0.25rem 0.6rem",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    borderRadius: "999px",
                    background: "rgba(217,119,6,0.12)",
                    color: "#d97706",
                    border: "1px solid #d97706",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  Awaiting moderation
                </span>
              ) : (
                <button
                  className="upvote-chip"
                  onClick={() => upvote(q.id)}
                  disabled={hasVoted || !canVote}
                  aria-pressed={hasVoted}
                  aria-label={
                    hasVoted
                      ? `You've upvoted (${q.upvote_count} total): ${q.body.slice(0, 60)}`
                      : `Upvote (${q.upvote_count} so far): ${q.body.slice(0, 60)}`
                  }
                  title={
                    !canVote
                      ? "Join the event first to upvote"
                      : hasVoted
                      ? "You've already upvoted this"
                      : "Upvote"
                  }
                  style={{
                    position: "absolute",
                    top: "0.5rem",
                    right: "0.5rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    padding: "0.35rem 0.7rem",
                    minHeight: "auto",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    borderRadius: "999px",
                    background: hasVoted ? "var(--accent)" : "var(--surface-elevated)",
                    color: hasVoted ? "var(--accent-fg)" : "var(--fg)",
                    border: "1px solid var(--border)",
                  }}
                >
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    {q.upvote_count}
                  </span>
                  <span aria-hidden="true">👍</span>
                </button>
              )}
              <div
                className="body"
                style={{
                  paddingRight: isPending ? "9rem" : "4.5rem",
                  ...(isAnswered ? { textDecoration: "line-through" } : null),
                }}
              >
                {q.body}
              </div>
              {askersLine && (
                <div className="meta" style={{ fontSize: "0.85rem", marginTop: "0.25rem" }}>
                  Asked by {askersLine}
                </div>
              )}
              {isAnswered && q.answer_text && (
                <div
                  className="meta"
                  style={{
                    marginTop: "0.5rem",
                    padding: "0.5rem",
                    background: "rgba(22,163,74,0.08)",
                    borderRadius: "4px",
                    fontSize: "0.9rem",
                  }}
                >
                  <strong>Answered:</strong> {q.answer_text}
                </div>
              )}
              <div className="meta">
                {isPending ? (
                  <>Only you can see this until a moderator approves it.</>
                ) : (
                  <>
                    {voicesCount} {voicesCount === 1 ? "voice" : "voices"}
                    {isAnswered && (
                      <span style={{ marginLeft: "0.5rem", color: "#16a34a", fontWeight: 600 }}>
                        · Answered
                      </span>
                    )}
                  </>
                )}
              </div>
            </li>
          );
        })}
            {visible.length === 0 && (
              <li className="card meta">No approved questions yet.</li>
            )}
          </ul>
        );
      })()}
      </div>
    </main>
  );
}
